// ============================================================================
// EDUSPHERE ENTERPRISE - ROUTER CONFIGURATION
// Production-ready routing with lazy loading and role-based access
// ============================================================================

import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, Outlet, useParams, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { useAuthStore } from '@/store';
import { canAccessRoute } from '@/config/routes';
import { ROLE_LANDING_ROUTE } from '@/config/roleLanding';

// ============================================================================
// LAZY LOAD LAYOUTS
// ============================================================================
const MainLayout = lazy(() => import('@/layouts/MainLayout'));
const AuthLayout = lazy(() => import('@/layouts/AuthLayout'));

// ============================================================================
// LAZY LOAD PAGES - AUTH
// ============================================================================
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));

// ============================================================================
// LAZY LOAD PAGES - DASHBOARD
// ============================================================================
const SuperAdminDashboard = lazy(() => import('@/pages/dashboard/SuperAdminDashboard'));
const PrincipalDashboard = lazy(() => import('@/pages/dashboard/PrincipalDashboard'));
const StudentDashboard = lazy(() => import('@/pages/dashboard/StudentDashboard'));
const ParentDashboard = lazy(() => import('@/pages/dashboard/ParentDashboard'));
const TeacherDashboard = lazy(() => import('@/pages/dashboard/TeacherDashboard'));

// ============================================================================
// LAZY LOAD PAGES - STUDENTS
// ============================================================================
const StudentList = lazy(() => import('@/pages/students/StudentList'));
const StudentProfile = lazy(() => import('@/pages/students/StudentProfile'));
const Certificates = lazy(() => import('@/pages/students/Certificates'));
const StudentNew = lazy(() => import('@/pages/students/StudentNew'));
const StudentEdit = lazy(() => import('@/pages/students/StudentEdit'));
const BatchAllocation = lazy(() => import('@/pages/students/BatchAllocation'));
const BatchAdmission = lazy(() => import('@/pages/students/BatchAdmission'));
const BulkImport = lazy(() => import('@/pages/students/BulkImport'));
const BulkExport = lazy(() => import('@/pages/students/BulkExport'));
const PromotionWizard = lazy(() => import('@/pages/students/PromotionWizard'));
const TransferStudents = lazy(() => import('@/pages/students/TransferStudents'));
const IDCardCenter = lazy(() => import('@/pages/students/IDCardCenter'));
// W4: Operations Center wraps StudentList + reports + at-risk + EWS audit.
// Legacy routes (/students/list, /students/at-risk, /students/reports/*)
// remain registered as they are subsumed by tabs inside the Operations Center.
const StudentsOperationsCenter = lazy(() => import('@/pages/students/StudentsOperationsCenter'));
const AtRiskStudents = lazy(() => import('@/pages/students/AtRiskStudents'));
// Stream E1 — students advanced business logic
const HouseSystem = lazy(() => import('@/pages/students/HouseSystem'));
// Wave 3 — activities + document checklists
const ActivitiesManager = lazy(() => import('@/pages/students/ActivitiesManager'));
const DocumentChecklistConfig = lazy(() => import('@/pages/students/DocumentChecklistConfig'));
const PhotoRefreshCenter = lazy(() => import('@/pages/students/PhotoRefreshCenter'));
const Readmission = lazy(() => import('@/pages/students/Readmission'));

// ============================================================================
// LAZY LOAD PAGES - CRM / ADMISSION
// ============================================================================
const LeadManagement = lazy(() => import('@/pages/crm/LeadManagement'));
const AdmissionDashboard = lazy(() => import('@/pages/admission/AdmissionDashboard'));
const VisitSchedule = lazy(() => import('@/pages/admission/VisitSchedule'));
const EntranceTests = lazy(() => import('@/pages/admission/EntranceTests'));
const Applications = lazy(() => import('@/pages/admission/Applications'));
const NewApplication = lazy(() => import('@/pages/admission/NewApplication'));
const ApprovalQueue = lazy(() => import('@/pages/admission/ApprovalQueue'));
const AdmissionWorkflowSettings = lazy(() => import('@/pages/admission/WorkflowSettings'));
const AdmissionLeadPipelineSettings = lazy(() => import('@/pages/admission/LeadPipelineSettings'));
const AdmissionLeadCadences = lazy(() => import('@/pages/admission/LeadCadences'));
const AutomationRules = lazy(() => import('@/pages/admission/AutomationRules'));
const AutomationRuleEditPage = lazy(() => import('@/pages/admission/AutomationRuleEditPage'));
const QuestionPaperBuilder = lazy(() => import('@/pages/admission/question-builder/QuestionPaperBuilder'));
const PaperPreview = lazy(() => import('@/pages/admission/question-builder/PaperPreview'));
const Evaluation = lazy(() => import('@/pages/admission/question-builder/Evaluation'));
const VisitConfirm = lazy(() => import('@/pages/public/VisitConfirm'));
// W6 — entrance-test candidate self-service (token-in-URL, no login).
const CandidateTest = lazy(() => import('@/pages/public/CandidateTest'));
// Report-card QR verification (token-in-URL, no login) — scanned from a
// printed report card's QR code.
const ReportCardVerify = lazy(() => import('@/pages/public/ReportCardVerify'));
// Public email-verification landing page (token-in-query, no login).
const VerifyEmail = lazy(() => import('@/pages/public/VerifyEmail'));

// ============================================================================
// LAZY LOAD PAGES - ATTENDANCE
// ============================================================================
const AttendanceMarking = lazy(() => import('@/pages/attendance/AttendanceMarking'));
const StaffAttendance = lazy(() => import('@/pages/attendance/StaffAttendance'));

// ============================================================================
// LAZY LOAD PAGES - ACADEMIC
// ============================================================================
const ClassesManagement = lazy(() => import('@/pages/academic/ClassesManagement'));
// Phase 3 (D2): SubjectsManagement, SubjectMapping, TeacherAssignment were
// merged into the single AcademicSetupStudio (4-step, deep-linkable via
// ?step=). The old routes below now redirect into it.
const AcademicSetupStudio = lazy(() => import('@/pages/academic/AcademicSetupStudio'));
const Timetable = lazy(() => import('@/pages/academic/Timetable'));
// Wave 2 — substitution board + room utilization
const SubstitutionBoard = lazy(() => import('@/pages/academic/SubstitutionBoard'));
const RoomUtilization = lazy(() => import('@/pages/academic/RoomUtilization'));
const AcademicCalendar = lazy(() => import('@/pages/academic/AcademicCalendar'));
const SyllabusTracking = lazy(() => import('@/pages/academic/SyllabusTracking'));
// Wave 4 — homework tracking + weekly lesson plans
const HomeworkManager = lazy(() => import('@/pages/academic/HomeworkManager'));
const LessonPlanner = lazy(() => import('@/pages/academic/LessonPlanner'));
const LessonPlanReview = lazy(() => import('@/pages/academic/LessonPlanReview'));
const LessonPlanHandout = lazy(() => import('@/pages/academic/LessonPlanHandout'));

// ============================================================================
// LAZY LOAD PAGES - EXAMINATION
// ============================================================================
const MarksEntryGrid = lazy(() => import('@/pages/examination/MarksEntryGrid'));
const ExamSchedule = lazy(() => import('@/pages/examination/ExamSchedule'));
const ReportCards = lazy(() => import('@/pages/examination/ReportCards'));
const GradeConfiguration = lazy(() => import('@/pages/examination/GradeConfiguration'));
const HallTickets = lazy(() => import('@/pages/examination/HallTickets'));
const ClassExamMapping = lazy(() => import('@/pages/examination/ClassExamMapping'));
const MarksModeration = lazy(() => import('@/pages/examination/MarksModeration'));
const GradeScales = lazy(() => import('@/pages/examination/GradeScales'));
const ReportDesigner = lazy(() => import('@/pages/examination/ReportDesigner'));
const GenerateReports = lazy(() => import('@/pages/examination/GenerateReports'));
const PublishResults = lazy(() => import('@/pages/examination/PublishResults'));
const PerformanceAnalysis = lazy(() => import('@/pages/examination/PerformanceAnalysis'));
const SubjectAnalysis = lazy(() => import('@/pages/examination/SubjectAnalysis'));
const ToppersList = lazy(() => import('@/pages/examination/ToppersList'));

// ============================================================================
// LAZY LOAD PAGES - FINANCE
// ============================================================================
const FeeCollectionPOS = lazy(() => import('@/pages/finance/FeeCollectionPOS'));
const FeeDesignerStudio = lazy(() => import('@/pages/finance/FeeDesignerStudio'));
const FeeStructure = lazy(() => import('@/pages/finance/FeeStructure'));
const FeeDefaulters = lazy(() => import('@/pages/finance/FeeDefaulters'));
const Concessions = lazy(() => import('@/pages/finance/Concessions'));
// E2 — advanced fees business logic pages
const FeeTerms = lazy(() => import('@/pages/finance/FeeTerms'));
const FeeBundles = lazy(() => import('@/pages/finance/FeeBundles'));
const RefundManagement = lazy(() => import('@/pages/finance/RefundManagement'));
const YearRollover = lazy(() => import('@/pages/finance/YearRollover'));
const ChequeBounceQueue = lazy(() => import('@/pages/finance/ChequeBounceQueue'));
const Expenses = lazy(() => import('@/pages/finance/Expenses'));
const FinanceReports = lazy(() => import('@/pages/finance/FinanceReports'));
const FinanceDashboard = lazy(() => import('@/pages/finance/FinanceDashboard'));
const StudentLedger = lazy(() => import('@/pages/finance/StudentLedger'));
const ReceiptArchive = lazy(() => import('@/pages/finance/ReceiptArchive'));
const DiscountsWaivers = lazy(() => import('@/pages/finance/DiscountsWaivers'));
const CollectionReport = lazy(() => import('@/pages/finance/CollectionReport'));
const OutstandingReport = lazy(() => import('@/pages/finance/OutstandingReport'));
const ClassWiseReport = lazy(() => import('@/pages/finance/ClassWiseReport'));
const FeeConfiguration = lazy(() => import('@/pages/finance/FeeConfiguration'));
const TaxConfiguration = lazy(() => import('@/pages/finance/TaxConfiguration'));
const BankReconciliation = lazy(() => import('@/pages/finance/BankReconciliation'));
const TrialBalance = lazy(() => import('@/pages/finance/TrialBalance'));
const GlReconciliation = lazy(() => import('@/pages/finance/GlReconciliation'));
const GstReport = lazy(() => import('@/pages/finance/GstReport'));
const VendorTdsReport = lazy(() => import('@/pages/finance/VendorTdsReport'));
const ProfitLoss = lazy(() => import('@/pages/finance/ProfitLoss'));
const BalanceSheet = lazy(() => import('@/pages/finance/BalanceSheet'));
const CashFlow = lazy(() => import('@/pages/finance/CashFlow'));
const JournalEntry = lazy(() => import('@/pages/finance/JournalEntry'));
// Wave 1 — installment plans, scholarships, audit trail
const InstallmentPlans = lazy(() => import('@/pages/finance/InstallmentPlans'));
const Scholarships = lazy(() => import('@/pages/finance/Scholarships'));
const FeeAuditTrail = lazy(() => import('@/pages/finance/FeeAuditTrail'));

// ============================================================================
// LAZY LOAD PAGES - HR
// ============================================================================
const LeaveManagement = lazy(() => import('@/pages/hr/LeaveManagement'));
const Recruitment = lazy(() => import('@/pages/hr/Recruitment'));
const Performance = lazy(() => import('@/pages/hr/Performance'));
const EmployeeSeparation = lazy(() => import('@/pages/hr/EmployeeSeparation'));
const Departments = lazy(() => import('@/pages/hr/Departments'));
const Designations = lazy(() => import('@/pages/hr/Designations'));
const SalaryStructures = lazy(() => import('@/pages/hr/SalaryStructures'));
const SalaryStructureDesigner = lazy(() => import('@/pages/hr/SalaryStructureDesigner'));
const Payslips = lazy(() => import('@/pages/hr/Payslips'));
const HRDashboard = lazy(() => import('@/pages/hr/HRDashboard'));

// ============================================================================
// LAZY LOAD PAGES - HR / REPORTS
// ============================================================================
const SalaryRegister = lazy(() => import('@/pages/hr/reports/SalaryRegister'));
const HeadcountReport = lazy(() => import('@/pages/hr/reports/HeadcountReport'));

// ============================================================================
// LAZY LOAD PAGES - HR / EMPLOYEES
// ============================================================================
const PayrollProcessor = lazy(() => import('@/pages/hr/PayrollProcessor'));
const PeopleOperationsCenter = lazy(() => import('@/pages/hr/PeopleOperationsCenter'));
const TalentCenter = lazy(() => import('@/pages/hr/TalentCenter'));
const ComplianceSettings = lazy(() => import('@/pages/hr/ComplianceSettings'));
const BonusReport = lazy(() => import('@/pages/hr/BonusReport'));
// W7 — Payroll & Tax configuration (PT slab manager / PF / ESI live
// calculators + bulk salary-structure assignment wizard).
const PayrollConfig = lazy(() => import('@/pages/hr/config/PayrollConfig'));
const EmployeeList = lazy(() => import('@/pages/employees/EmployeeList'));

// ============================================================================
// LAZY LOAD PAGES - TRANSPORT
// ============================================================================
const TransportManagement = lazy(() => import('@/pages/transport/TransportManagement'));
const TransportVehicles = lazy(() => import('@/pages/transport/Vehicles'));
const LiveTracking = lazy(() => import('@/pages/transport/LiveTracking'));
const TransportFees = lazy(() => import('@/pages/transport/TransportFees'));
const StudentAssignment = lazy(() => import('@/pages/transport/StudentAssignment'));
const TransportBilling = lazy(() => import('@/pages/transport/TransportBilling'));
const RouteWiseReport = lazy(() => import('@/pages/transport/RouteWiseReport'));
const UtilizationReport = lazy(() => import('@/pages/transport/UtilizationReport'));

// ============================================================================
// LAZY LOAD PAGES - LIBRARY
// ============================================================================
const LibraryManagement = lazy(() => import('@/pages/library/LibraryManagement'));
const LibraryCatalog = lazy(() => import('@/pages/library/Catalog'));
const LibraryIssueReturn = lazy(() => import('@/pages/library/IssueReturn'));
const LibraryReports = lazy(() => import('@/pages/library/LibraryReports'));
const Transactions = lazy(() => import('@/pages/library/Transactions'));
const Fines = lazy(() => import('@/pages/library/Fines'));
const CirculationReport = lazy(() => import('@/pages/library/CirculationReport'));
const OverdueReport = lazy(() => import('@/pages/library/OverdueReport'));
const PopularBooks = lazy(() => import('@/pages/library/PopularBooks'));

// ============================================================================
// LAZY LOAD PAGES - HOSTEL
// ============================================================================
const HostelRooms = lazy(() => import('@/pages/hostel/Rooms'));
const HostelAllocation = lazy(() => import('@/pages/hostel/Allocation'));
const HostelFees = lazy(() => import('@/pages/hostel/HostelFees'));
const MessManagement = lazy(() => import('@/pages/hostel/MessManagement'));

// ============================================================================
// LAZY LOAD PAGES - ERROR / NOT FOUND
// ============================================================================
const NotFound = lazy(() => import('@/pages/NotFound'));
const NotAuthorized = lazy(() => import('@/pages/NotAuthorized'));

// ============================================================================
// LAZY LOAD PAGES - COMMUNICATION
// ============================================================================
const Announcements = lazy(() => import('@/pages/communication/Announcements'));
const Messages = lazy(() => import('@/pages/communication/Messages'));
const SMSGateway = lazy(() => import('@/pages/communication/SMSGateway'));

// ============================================================================
// LAZY LOAD PAGES - VISUAL TEMPLATE EDITOR
// ============================================================================
const TemplateLibrary = lazy(() => import('@/pages/templates/TemplateLibrary'));
const TemplateEditorPage = lazy(() => import('@/pages/templates/TemplateEditorPage'));

// ============================================================================
// LAZY LOAD PAGES - SETTINGS
// ============================================================================
const GeneralSettings = lazy(() => import('@/pages/settings/GeneralSettings'));
const MyProfile = lazy(() => import('@/pages/settings/MyProfile'));
const UserManagement = lazy(() => import('@/pages/settings/UserManagement'));
const RolesPermissions = lazy(() => import('@/pages/settings/RolesPermissions'));
const SchoolSettings = lazy(() => import('@/pages/settings/SchoolSettings'));
const SectionAssignmentRules = lazy(() => import('@/pages/settings/SectionAssignmentRules'));
const Branches = lazy(() => import('@/pages/settings/Branches'));
const AcademicYears = lazy(() => import('@/pages/settings/AcademicYears'));

// ============================================================================
// LAZY LOAD PAGES - STUDENTS (Additional)
// ============================================================================
// Guardians page removed — guardian data is in student profile
const StrengthReport = lazy(() => import('@/pages/students/reports/StrengthReport'));
const DemographicsReport = lazy(() => import('@/pages/students/reports/DemographicsReport'));
const CategoryReport = lazy(() => import('@/pages/students/reports/CategoryReport'));
const RteIntakeReport = lazy(() => import('@/pages/students/reports/RteIntakeReport'));
const RteAnnexureIvForm = lazy(() => import('@/pages/students/forms/RteAnnexureIvForm'));


// ============================================================================
// LAZY LOAD PAGES - ATTENDANCE (Additional)
// ============================================================================
const AttendanceReports = lazy(() => import('@/pages/attendance/AttendanceReports'));
const AttendanceLeaveManagement = lazy(() => import('@/pages/attendance/LeaveManagement'));

// ============================================================================
// LAZY LOAD PAGES - ADMISSION REPORTS
// ============================================================================
const AdmissionFunnel = lazy(() => import('@/pages/admission/reports/AdmissionFunnel'));
const AdmissionAnalytics = lazy(() => import('@/pages/admission/reports/AdmissionAnalytics'));
const SourceAnalysis = lazy(() => import('@/pages/admission/reports/SourceAnalysis'));
const SeatInventory = lazy(() => import('@/pages/admission/SeatInventory'));
const Waitlist = lazy(() => import('@/pages/admission/Waitlist'));
const CommunicationTemplates = lazy(() => import('@/pages/admission/CommunicationTemplates'));
const AdmissionReportsDashboard = lazy(() => import('@/pages/admission/reports/ReportsDashboard'));
const CounsellorPerformanceReport = lazy(() => import('@/pages/admission/reports/CounsellorPerformance'));
const ClassDemandReport = lazy(() => import('@/pages/admission/reports/ClassDemand'));
const TimeToConvertReport = lazy(() => import('@/pages/admission/reports/TimeToConvert'));
const DocumentComplianceReport = lazy(() => import('@/pages/admission/reports/DocumentCompliance'));
const AdmissionFinancialReportPage = lazy(() => import('@/pages/admission/reports/AdmissionFinancialReport'));
const LostLeadAnalysisReport = lazy(() => import('@/pages/admission/reports/LostLeadAnalysis'));

// ============================================================================
// LAZY LOAD PAGES - COMMUNICATION (Additional)
// ============================================================================
const EmailTemplates = lazy(() => import('@/pages/communication/EmailTemplates'));
const Templates = lazy(() => import('@/pages/communication/Templates'));

// W9 Phase 2 — new comms_* pipeline pages
const CommsConsole = lazy(() => import('@/pages/communication/CommsConsole'));
const ComposeAndSend = lazy(() => import('@/pages/communication/ComposeAndSend'));
const TemplatesManager = lazy(() => import('@/pages/communication/TemplatesManager'));
const MessageThread = lazy(() => import('@/pages/communication/MessageThread'));
const DeliveryLogsAndDLQ = lazy(() => import('@/pages/communication/DeliveryLogsAndDLQ'));
const ConsentAndDNDCenter = lazy(() => import('@/pages/communication/ConsentAndDNDCenter'));
const PTMManager = lazy(() => import('@/pages/communication/PTMManager'));

// ============================================================================
// LAZY LOAD PAGES - INVENTORY
// ============================================================================
const Items = lazy(() => import('@/pages/inventory/Items'));
const Vendors = lazy(() => import('@/pages/inventory/Vendors'));
const PurchaseOrders = lazy(() => import('@/pages/inventory/PurchaseOrders'));
const StockMovements = lazy(() => import('@/pages/inventory/StockMovements'));
const Assets = lazy(() => import('@/pages/inventory/Assets'));
const AssetAllocation = lazy(() => import('@/pages/inventory/AssetAllocation'));
const StockReport = lazy(() => import('@/pages/inventory/StockReport'));
const LowStockReport = lazy(() => import('@/pages/inventory/LowStockReport'));
const AssetRegister = lazy(() => import('@/pages/inventory/AssetRegister'));
const AuditTrail = lazy(() => import('@/pages/inventory/AuditTrail'));

// ============================================================================
// LAZY LOAD PAGES - ATTENDANCE (Extended)
// ============================================================================
const LeaveRequests = lazy(() => import('@/pages/attendance/LeaveRequests'));
const LeaveApproval = lazy(() => import('@/pages/attendance/LeaveApproval'));
const LeaveBalance = lazy(() => import('@/pages/attendance/LeaveBalance'));
const DailyReport = lazy(() => import('@/pages/attendance/DailyReport'));
const MonthlyReport = lazy(() => import('@/pages/attendance/MonthlyReport'));
const BulkAttendanceView = lazy(() => import('@/pages/attendance/BulkAttendanceView'));
const AbsenteeReport = lazy(() => import('@/pages/attendance/AbsenteeReport'));
const IntelligenceReport = lazy(() => import('@/pages/attendance/reports/Intelligence'));
// W7 — new attendance pages
const LeaveCenterStudents = lazy(() => import('@/pages/attendance/LeaveCenterStudents'));
const DevicesAndBiometric = lazy(() => import('@/pages/attendance/DevicesAndBiometric'));
const MidDayMealRegister = lazy(() => import('@/pages/attendance/MidDayMealRegister'));
// E4 — Attendance advanced business logic
const E4AcademicCalendar = lazy(() => import('@/pages/attendance/AcademicCalendar'));
const E4LateEntryRules = lazy(() => import('@/pages/attendance/LateEntryRules'));
const E4OutdoorExemptions = lazy(() => import('@/pages/attendance/OutdoorExemptions'));
const E4MassBunkReview = lazy(() => import('@/pages/attendance/MassBunkReview'));
const E4EarlyLeaveSignout = lazy(() => import('@/pages/attendance/EarlyLeaveSignout'));
const E4SubjectAttendance = lazy(() => import('@/pages/attendance/SubjectAttendance'));
const E4GeofenceConfig = lazy(() => import('@/pages/attendance/GeofenceConfig'));

// ============================================================================
// LAZY LOAD PAGES - SETTINGS (Extended)
// ============================================================================
const Integrations = lazy(() => import('@/pages/settings/Integrations'));
const NotificationTemplates = lazy(() => import('@/pages/settings/NotificationTemplates'));
const CustomFields = lazy(() => import('@/pages/settings/CustomFields'));
const AuditLogs = lazy(() => import('@/pages/settings/AuditLogs'));
const ApprovalWorkflows = lazy(() => import('@/pages/settings/ApprovalWorkflows'));
const SetupWizard = lazy(() => import('@/pages/settings/SetupWizard'));

// ============================================================================
// LAZY LOAD PAGES - ACADEMIC (Extended)
// ============================================================================
const ClassStrengthReport = lazy(() => import('@/pages/academic/ClassStrengthReport'));
const TeacherWorkloadReport = lazy(() => import('@/pages/academic/TeacherWorkloadReport'));

// ============================================================================
// LAZY LOAD PAGES - REPORTS
// ============================================================================
const ReportsDashboard = lazy(() => import('@/pages/reports/ReportsDashboard'));
const CustomReports = lazy(() => import('@/pages/reports/CustomReports'));
const ConsolidatedReports = lazy(() => import('@/pages/reports/ConsolidatedReports'));
const ReportBuilder = lazy(() => import('@/pages/reports/ReportBuilder'));

// ============================================================================
// LAZY LOAD PAGES - PHASE 0+ NEW PAGES (TN Matric rebuild)
// ============================================================================
const FeeComponents = lazy(() => import('@/pages/finance/FeeComponents'));
const SiblingGroups = lazy(() => import('@/pages/students/SiblingGroups'));
const PaymentDue = lazy(() => import('@/pages/students/PaymentDue'));
const AccountantDashboard = lazy(() => import('@/pages/dashboard/AccountantDashboard'));
const HRManagerDashboard = lazy(() => import('@/pages/dashboard/HRManagerDashboard'));
const AcademicDashboard = lazy(() => import('@/pages/academic/AcademicDashboard'));
const ExamSeating = lazy(() => import('@/pages/academic/ExamSeating'));
const ExamDashboard = lazy(() => import('@/pages/examination/ExamDashboard'));
const SeatingPlan = lazy(() => import('@/pages/examination/SeatingPlan'));
// Phase 2 W6 — Exam single-pane shells
const ExamSetupCenter = lazy(() => import('@/pages/examination/ExamSetupCenter'));
const MarksEntryAndModeration = lazy(() => import('@/pages/examination/MarksEntryAndModeration'));
const ResultsAndReportCards = lazy(() => import('@/pages/examination/ResultsAndReportCards'));
const HallTicketsAndSeating = lazy(() => import('@/pages/examination/HallTicketsAndSeating'));
// Stream E3 — Examinations advanced business logic
const MarksSplitEditor = lazy(() => import('@/pages/examination/MarksSplitEditor'));
const StreamConfiguration = lazy(() => import('@/pages/examination/StreamConfiguration'));
const SupplementaryExams = lazy(() => import('@/pages/examination/SupplementaryExams'));
const BellCurveModeration = lazy(() => import('@/pages/examination/BellCurveModeration'));
const PracticalScheduling = lazy(() => import('@/pages/examination/PracticalScheduling'));
const ExamExemptions = lazy(() => import('@/pages/examination/ExamExemptions'));
const ExamAnalytics = lazy(() => import('@/pages/examination/ExamAnalytics'));
// Wave 1 — report card batch generation
const ReportCardBatches = lazy(() => import('@/pages/examination/ReportCardBatches'));
// W1 — Question Paper Studio
const QuestionPaperStudio = lazy(() => import('@/pages/examination/question-studio/QuestionPaperStudio'));
// NEP 2020 Holistic Progress Card (HPC)
const HolisticProgressCard = lazy(() => import('@/pages/examination/HolisticProgressCard'));
const SalaryComponents = lazy(() => import('@/pages/hr/SalaryComponents'));
const SalaryAdvances = lazy(() => import('@/pages/hr/SalaryAdvances'));
const ReimbursementClaims = lazy(() => import('@/pages/hr/ReimbursementClaims'));
const Gratuity = lazy(() => import('@/pages/hr/Gratuity'));
const EmployeeProfile = lazy(() => import('@/pages/employees/EmployeeProfile'));
const EmployeeNew = lazy(() => import('@/pages/employees/EmployeeNew'));
const EmployeeDocuments = lazy(() => import('@/pages/employees/EmployeeDocuments'));
// EmployeeDocuments is a prop-driven sub-component; this wrapper adapts it to a
// standalone route by sourcing employeeId from the :id path param.
const EmployeeDocumentsRoute = () => {
  const { id } = useParams();
  return <EmployeeDocuments employeeId={id ?? ''} />;
};
// Stream E5 — HR/Payroll advanced business logic
const DARevision = lazy(() => import('@/pages/hr/DARevision'));
const AnnualIncrements = lazy(() => import('@/pages/hr/AnnualIncrements'));
const SalaryArrears = lazy(() => import('@/pages/hr/SalaryArrears'));
const LeaveEncashment = lazy(() => import('@/pages/hr/LeaveEncashment'));
const EmployeeLoansE5 = lazy(() => import('@/pages/hr/EmployeeLoans'));
const BankFileExportE5 = lazy(() => import('@/pages/hr/BankFileExport'));
const Form24QFilingE5 = lazy(() => import('@/pages/hr/Form24QFiling'));

// ============================================================================
// LAZY LOAD PAGES - MODULES HOME
// ============================================================================
const ModulesHome = lazy(() => import('@/pages/ModulesHome'));

// ============================================================================
// LOADING COMPONENTS — Spatial Flow Design
// ============================================================================
const PageLoader = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4">
    <div className="relative flex items-center justify-center">
      <div className="absolute size-16 animate-ping rounded-2xl bg-primary/10" />
      <div className="relative flex size-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-depth-2">
        <span className="font-display text-xl font-bold">S</span>
      </div>
    </div>
    <p className="text-sm font-medium text-muted-foreground animate-pulse">
      Loading Suvadhi...
    </p>
  </div>
);

const ContentLoader = () => (
  <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
    <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10">
      <div className="size-5 animate-spin-slow rounded-full border-2 border-primary/30 border-t-primary" />
    </div>
    <p className="text-xs text-muted-foreground">Loading content...</p>
  </div>
);

// Suspense wrapper for layouts
const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

// Suspense wrapper for page content
const PageSuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<ContentLoader />}>{children}</Suspense>
);

// ============================================================================
// PLACEHOLDER COMPONENT FOR ROUTES WITHOUT PAGES
// ============================================================================
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="flex min-h-[60vh] flex-col items-center justify-center p-6 text-center">
    <div className="flex size-20 items-center justify-center rounded-3xl bg-muted/50 mb-4">
      <span className="text-4xl opacity-60">🚧</span>
    </div>
    <h2 className="font-display text-2xl font-bold text-foreground">{title}</h2>
    <p className="mt-2 max-w-md text-sm text-muted-foreground">
      This feature is currently under development. Please check back soon.
    </p>
    <div className="mt-5 flex gap-3">
      <button
        onClick={() => window.history.back()}
        className="inline-flex h-10 items-center rounded-xl border border-border/30 bg-card/50 px-4 text-sm font-medium transition-all hover:bg-card/80"
      >
        Go Back
      </button>
      <a
        href="/modules"
        className="inline-flex h-10 items-center rounded-xl bg-primary px-4 text-sm font-medium text-primary-foreground shadow-depth-1 transition-all hover:bg-primary/90"
      >
        Go to Modules
      </a>
    </div>
  </div>
);

// ============================================================================
// AUTH CHECK
// ============================================================================

// ============================================================================
// ROUTE WRAPPERS
// ============================================================================
// RUNTIME ROUTE-LEVEL RBAC GUARD
// Single chokepoint. Reads the LIVE roles from the Zustand store (not stale
// localStorage) and the CURRENT pathname on every navigation, then decides
// whether to render the app shell or a NotAuthorized page.
//
// Policy (see canAccessRoute in config/routes.ts):
//   - super_admin / admin bypass everything.
//   - Exact ROUTE_METADATA roles are most specific and decide alone.
//   - Otherwise DEFAULT-DENY by module: the first path segment must be in
//     MODULE_ROUTE_ACCESS and include one of the user's roles (detail routes
//     like /students/:id inherit the module audience). Unknown segments block.
// NotAuthorized renders in place (no auto-redirect) so there is never a loop.
// ============================================================================
const RouteGuard = () => {
  const { pathname } = useLocation();
  const roles = useAuthStore((state) => state.user?.roles);

  // No roles resolved yet (e.g. store rehydrating) — fail open to avoid a
  // transient lockout; isAuthenticated() already gated entry to this subtree.
  const allowed = !roles || roles.length === 0 || canAccessRoute(pathname, roles);

  if (!allowed) {
    return (
      <PageSuspense>
        <NotAuthorized />
      </PageSuspense>
    );
  }

  return <MainLayout />;
};

// ----------------------------------------------------------------------------
// F5 — /admission/workflow-settings exposes the FULL approval-workflow config
// (allowed roles/designations per stage, SLA/escalation policy) on GET. The
// in-page `canManage` gate in WorkflowSettings.tsx only disables editing — it
// still renders that config to anyone who can mount the page. Add an explicit
// allow-list on top of the module-level /admission guard so any non-admission
// role (parent, student, teacher, ...) is redirected to NotAuthorized instead
// of ever mounting the settings page. Mirrors WorkflowSettings.tsx's own
// MANAGE_ROLES list (kept in sync manually — both are small, static lists).
// ----------------------------------------------------------------------------
const WORKFLOW_SETTINGS_ROLES = ['super_admin', 'principal', 'vice_principal', 'admin_staff'];

const AdmissionWorkflowSettingsRoute = () => {
  const roles = useAuthStore((state) => state.user?.roles);
  // Fail open only while roles haven't resolved yet (store rehydrating) — the
  // outer RouteGuard/isAuthenticated checks already gated entry to this tree.
  const allowed = !roles || roles.length === 0 || roles.some((role) => WORKFLOW_SETTINGS_ROLES.includes(role));

  if (!allowed) {
    return (
      <PageSuspense>
        <NotAuthorized />
      </PageSuspense>
    );
  }

  return (
    <PageSuspense>
      <AdmissionWorkflowSettings />
    </PageSuspense>
  );
};

// Lead Pipeline Settings shares the same admission-manage allow-list — the page
// exposes the full lead transition graph, so gate any non-admission role out.
const AdmissionLeadPipelineSettingsRoute = () => {
  const roles = useAuthStore((state) => state.user?.roles);
  const allowed = !roles || roles.length === 0 || roles.some((role) => WORKFLOW_SETTINGS_ROLES.includes(role));

  if (!allowed) {
    return (
      <PageSuspense>
        <NotAuthorized />
      </PageSuspense>
    );
  }

  return (
    <PageSuspense>
      <AdmissionLeadPipelineSettings />
    </PageSuspense>
  );
};

// Lead Cadences (follow-up sequence builder) — same admission-manage allow-list
// as pipeline/workflow settings, since it can send messages and create tasks.
const AdmissionLeadCadencesRoute = () => {
  const roles = useAuthStore((state) => state.user?.roles);
  const allowed = !roles || roles.length === 0 || roles.some((role) => WORKFLOW_SETTINGS_ROLES.includes(role));

  if (!allowed) {
    return (
      <PageSuspense>
        <NotAuthorized />
      </PageSuspense>
    );
  }

  return (
    <PageSuspense>
      <AdmissionLeadCadences />
    </PageSuspense>
  );
};

// Automation Rules shares the same admission-manage allow-list — the page can
// mutate leads/applications, so gate any non-admission role out.
const AutomationRulesRoute = () => {
  const roles = useAuthStore((state) => state.user?.roles);
  const allowed = !roles || roles.length === 0 || roles.some((role) => WORKFLOW_SETTINGS_ROLES.includes(role));

  if (!allowed) {
    return (
      <PageSuspense>
        <NotAuthorized />
      </PageSuspense>
    );
  }

  return (
    <PageSuspense>
      <AutomationRules />
    </PageSuspense>
  );
};

// The full-page rule editor (new / :id/edit) shares the same gate as the list.
const AutomationRuleEditRoute = () => {
  const roles = useAuthStore((state) => state.user?.roles);
  const allowed = !roles || roles.length === 0 || roles.some((role) => WORKFLOW_SETTINGS_ROLES.includes(role));

  if (!allowed) {
    return (
      <PageSuspense>
        <NotAuthorized />
      </PageSuspense>
    );
  }

  return (
    <PageSuspense>
      <AutomationRuleEditPage />
    </PageSuspense>
  );
};

const ProtectedRoute = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitialized = useAuthStore((s) => s.isInitialized);

  // Don't decide either way until the bootstrap GET /auth/me (see main.tsx)
  // has resolved against the httpOnly session cookie — deciding early on
  // stale/absent local state is exactly what produced the login↔dashboard
  // flicker on a fresh load.
  if (!isInitialized) {
    return <PageLoader />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }
  return (
    <ErrorBoundary>
      <SuspenseWrapper>
        <RouteGuard />
      </SuspenseWrapper>
    </ErrorBoundary>
  );
};

const PublicRoute = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const roles = useAuthStore((s) => s.user?.roles);

  if (!isInitialized) {
    return <PageLoader />;
  }
  if (isAuthenticated) {
    const landing = roles?.map((r) => ROLE_LANDING_ROUTE[r]).find(Boolean) || '/dashboard';
    return <Navigate to={landing} replace />;
  }
  return (
    <SuspenseWrapper>
      <AuthLayout />
    </SuspenseWrapper>
  );
};

// ============================================================================
// ROLE-BASED DASHBOARD COMPONENT
// ============================================================================
const RoleBasedDashboard = () => {
  const roles = useAuthStore((s) => s.user?.roles) || [];
  let userRole = 'teacher'; // safe default - not admin

  if (roles.includes('student')) {
    userRole = 'student';
  } else if (roles.includes('parent')) {
    userRole = 'parent';
  } else if (roles.includes('accountant')) {
    userRole = 'accountant';
  } else if (roles.includes('hr_manager')) {
    userRole = 'hr_manager';
  } else if (roles.includes('teacher') || roles.includes('class_teacher')) {
    userRole = 'teacher';
  } else if (roles.includes('principal') || roles.includes('vice_principal') || roles.includes('academic_head')) {
    userRole = 'principal';
  } else if (roles.includes('super_admin')) {
    userRole = 'super_admin';
  }

  switch (userRole) {
    case 'student':
      return <Suspense fallback={<ContentLoader />}><StudentDashboard /></Suspense>;
    case 'parent':
      return <Suspense fallback={<ContentLoader />}><ParentDashboard /></Suspense>;
    case 'accountant':
      return <Suspense fallback={<ContentLoader />}><AccountantDashboard /></Suspense>;
    case 'hr_manager':
      return <Suspense fallback={<ContentLoader />}><HRManagerDashboard /></Suspense>;
    case 'teacher':
      return <Suspense fallback={<ContentLoader />}><TeacherDashboard /></Suspense>;
    case 'principal':
      return <Suspense fallback={<ContentLoader />}><PrincipalDashboard /></Suspense>;
    case 'super_admin':
      return <Suspense fallback={<ContentLoader />}><SuperAdminDashboard /></Suspense>;
    default:
      return <Suspense fallback={<ContentLoader />}><TeacherDashboard /></Suspense>;
  }
};

// ============================================================================
// ROUTER CONFIGURATION
// ============================================================================
export const router = createBrowserRouter([
  // ========================================
  // PUBLIC ROUTES (AUTH)
  // ========================================
  {
    path: '/auth',
    element: <PublicRoute />,
    children: [
      { path: 'login', element: <LoginPage /> },
      { index: true, element: <Navigate to="login" replace /> },
    ],
  },

  // Open parent visit-confirmation page (Phase 5) — token-in-URL, no login.
  { path: '/visit-confirm/:token', element: <PageSuspense><VisitConfirm /></PageSuspense> },

  // Open entrance-test candidate self-service page (W6) — token-in-URL, no
  // login, no app shell (mobile-first standalone page, mirrors /visit-confirm).
  { path: '/entrance-test/:token', element: <PageSuspense><CandidateTest /></PageSuspense> },

  // Open report-card QR verification page — token-in-URL, no login, no app
  // shell (mirrors /visit-confirm and /entrance-test above).
  { path: '/verify/report-card/:token', element: <PageSuspense><ReportCardVerify /></PageSuspense> },

  // Public email-verification landing page — reached via /verify-email?token=...
  // (link sent in the verification email). Token-in-query, no login, no app
  // shell (mirrors /visit-confirm and /verify/report-card above). Registered
  // outside the /auth PublicRoute wrapper because it must work for a visitor
  // regardless of whether they currently have a session (PublicRoute would
  // otherwise redirect an already-authenticated user away before the token
  // gets verified).
  { path: '/verify-email', element: <PageSuspense><VerifyEmail /></PageSuspense> },

  // ========================================
  // PROTECTED ROUTES
  // ========================================
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      // Root redirect to modules home
      { index: true, element: <Navigate to="/modules" replace /> },

      // Modules Home
      { path: 'modules', element: <PageSuspense><ModulesHome /></PageSuspense> },

      // Dashboard - Role-based
      { path: 'dashboard', element: <RoleBasedDashboard /> },

      // ====================================
      // ADMISSION / CRM MODULE
      // ====================================
      {
        path: 'admission',
        children: [
          { index: true, element: <PageSuspense><AdmissionDashboard /></PageSuspense> },
          { path: 'leads', element: <PageSuspense><LeadManagement /></PageSuspense> },
          // Legacy Lead Board consolidated into the single Lead Management workspace.
          { path: 'leads/board', element: <Navigate to="/admission/leads" replace /> },
          { path: 'visits', element: <PageSuspense><VisitSchedule /></PageSuspense> },
          { path: 'tests', element: <PageSuspense><EntranceTests /></PageSuspense> },
          { path: 'question-builder', element: <PageSuspense><QuestionPaperBuilder /></PageSuspense> },
          { path: 'question-builder/:id', element: <PageSuspense><QuestionPaperBuilder /></PageSuspense> },
          { path: 'question-builder/preview/:id', element: <PageSuspense><PaperPreview /></PageSuspense> },
          { path: 'question-builder/evaluate/:id', element: <PageSuspense><Evaluation /></PageSuspense> },
          { path: 'applications', element: <PageSuspense><Applications /></PageSuspense> },
          { path: 'applications/new', element: <PageSuspense><NewApplication /></PageSuspense> },
          { path: 'approval-queue', element: <PageSuspense><ApprovalQueue /></PageSuspense> },
          { path: 'workflow-settings', element: <AdmissionWorkflowSettingsRoute /> },
          { path: 'lead-pipeline-settings', element: <AdmissionLeadPipelineSettingsRoute /> },
          { path: 'cadences', element: <AdmissionLeadCadencesRoute /> },
          { path: 'automation-rules', element: <AutomationRulesRoute /> },
          { path: 'automation-rules/new', element: <AutomationRuleEditRoute /> },
          { path: 'automation-rules/:id/edit', element: <AutomationRuleEditRoute /> },
          { path: 'seat-inventory', element: <PageSuspense><SeatInventory /></PageSuspense> },
          { path: 'waitlist', element: <PageSuspense><Waitlist /></PageSuspense> },
          { path: 'templates', element: <PageSuspense><CommunicationTemplates /></PageSuspense> },
          { path: 'reports', element: <PageSuspense><AdmissionReportsDashboard /></PageSuspense> },
          { path: 'reports/funnel', element: <PageSuspense><AdmissionFunnel /></PageSuspense> },
          { path: 'reports/analytics', element: <PageSuspense><AdmissionAnalytics /></PageSuspense> },
          { path: 'reports/source-analysis', element: <PageSuspense><SourceAnalysis /></PageSuspense> },
          { path: 'reports/counsellor-performance', element: <PageSuspense><CounsellorPerformanceReport /></PageSuspense> },
          { path: 'reports/class-demand', element: <PageSuspense><ClassDemandReport /></PageSuspense> },
          { path: 'reports/time-to-convert', element: <PageSuspense><TimeToConvertReport /></PageSuspense> },
          { path: 'reports/document-compliance', element: <PageSuspense><DocumentComplianceReport /></PageSuspense> },
          { path: 'reports/financial', element: <PageSuspense><AdmissionFinancialReportPage /></PageSuspense> },
          { path: 'reports/lost-leads', element: <PageSuspense><LostLeadAnalysisReport /></PageSuspense> },
        ],
      },
      // Legacy /crm aliases — consolidated into the single admission lead workspace.
      {
        path: 'crm',
        children: [
          { index: true, element: <Navigate to="/admission/leads" replace /> },
          { path: 'leads', element: <Navigate to="/admission/leads" replace /> },
        ],
      },

      // ====================================
      // STUDENTS MODULE
      // ====================================
      {
        path: 'students',
        children: [
          { index: true, element: <StudentList /> },
          { path: 'list', element: <StudentList /> },
          // W4: unified Operations Center (legacy routes kept above for redirect parity)
          { path: 'operations', element: <PageSuspense><StudentsOperationsCenter /></PageSuspense> },
          { path: 'new', element: <StudentNew /> },
          { path: 'create', element: <StudentNew /> },
          { path: 'batch-admission', element: <PageSuspense><BatchAdmission /></PageSuspense> },
          { path: 'batches', element: <PageSuspense><BatchAllocation /></PageSuspense> },
          { path: 'bulk-import', element: <PageSuspense><BulkImport /></PageSuspense> },
          { path: 'bulk-export', element: <PageSuspense><BulkExport /></PageSuspense> },
          { path: 'bulk-promotion', element: <PageSuspense><PromotionWizard /></PageSuspense> },
          { path: 'bulk-transfer', element: <PageSuspense><TransferStudents /></PageSuspense> },
          { path: 'id-cards', element: <PageSuspense><IDCardCenter /></PageSuspense> },
          { path: 'sibling-groups', element: <PageSuspense><SiblingGroups /></PageSuspense> },
          { path: 'payment-due', element: <PageSuspense><PaymentDue /></PageSuspense> },
          { path: 'reports/strength', element: <PageSuspense><StrengthReport /></PageSuspense> },
          { path: 'reports/demographics', element: <PageSuspense><DemographicsReport /></PageSuspense> },
          { path: 'reports/category', element: <PageSuspense><CategoryReport /></PageSuspense> },
          { path: 'reports/rte-intake', element: <PageSuspense><RteIntakeReport /></PageSuspense> },
          // Stream E1 — Students advanced business logic
          { path: 'house-system', element: <PageSuspense><HouseSystem /></PageSuspense> },
          { path: 'photo-refresh', element: <PageSuspense><PhotoRefreshCenter /></PageSuspense> },
          { path: 'readmission', element: <PageSuspense><Readmission /></PageSuspense> },
          // Wave 3 — activities + document checklists
          { path: 'activities', element: <PageSuspense><ActivitiesManager /></PageSuspense> },
          { path: 'document-checklists', element: <PageSuspense><DocumentChecklistConfig /></PageSuspense> },
          // Explicit at-risk route — without this, /students/at-risk fell through to
          // the `:id` param route and triggered a bogus StudentProfile fetch (id='at-risk').
          { path: 'at-risk', element: <PageSuspense><AtRiskStudents /></PageSuspense> },
          { path: 'certificates', element: <PageSuspense><Certificates /></PageSuspense> },
          // Resolve a student by admission number: /students/lookup?admission=25-26/00001
          { path: 'lookup', element: <StudentProfile /> },
          { path: ':id', element: <StudentProfile /> },
          { path: ':id/profile', element: <StudentProfile /> },
          { path: ':id/edit', element: <PageSuspense><StudentEdit /></PageSuspense> },
          { path: ':id/rte-annexure-iv', element: <PageSuspense><RteAnnexureIvForm /></PageSuspense> },
        ],
      },

      // Guardians page removed — guardian data is managed within student profile

      // ====================================
      // ACADEMIC MODULE
      // ====================================
      {
        path: 'academic',
        children: [
          { index: true, element: <PageSuspense><ClassesManagement /></PageSuspense> },
          { path: 'classes', element: <PageSuspense><ClassesManagement /></PageSuspense> },
          // Phase 3 (D2): Academic Setup Studio — 4-step, deep-linked via ?step=.
          { path: 'setup', element: <PageSuspense><AcademicSetupStudio /></PageSuspense> },
          // Legacy standalone-page URLs redirect into the studio (deep-linkable, bookmarks keep working).
          { path: 'subjects', element: <Navigate to="/academic/setup?step=subjects" replace /> },
          { path: 'subject-mapping', element: <Navigate to="/academic/setup?step=matrix" replace /> },
          { path: 'teacher-assignment', element: <Navigate to="/academic/setup?step=teachers" replace /> },
          { path: 'syllabus', element: <PageSuspense><SyllabusTracking /></PageSuspense> },
          { path: 'academic-calendar', element: <PageSuspense><AcademicCalendar /></PageSuspense> },
          { path: 'calendar', element: <PageSuspense><AcademicCalendar /></PageSuspense> },
          { path: 'timetable', element: <PageSuspense><Timetable /></PageSuspense> },
          { path: 'dashboard', element: <PageSuspense><AcademicDashboard /></PageSuspense> },
          { path: 'exam-halls', element: <PageSuspense><ExamSeating /></PageSuspense> },
          { path: 'reports/class-strength', element: <PageSuspense><ClassStrengthReport /></PageSuspense> },
          { path: 'reports/teacher-workload', element: <PageSuspense><TeacherWorkloadReport /></PageSuspense> },
          // Wave 2 — daily operations
          { path: 'substitutions', element: <PageSuspense><SubstitutionBoard /></PageSuspense> },
          { path: 'room-utilization', element: <PageSuspense><RoomUtilization /></PageSuspense> },
          // Wave 4 — homework tracking + weekly lesson plans
          { path: 'homework', element: <PageSuspense><HomeworkManager /></PageSuspense> },
          { path: 'lesson-planner', element: <PageSuspense><LessonPlanner /></PageSuspense> },
          { path: 'lesson-planner/:id/handout', element: <PageSuspense><LessonPlanHandout /></PageSuspense> },
          { path: 'lesson-plan-review', element: <PageSuspense><LessonPlanReview /></PageSuspense> },
        ],
      },

      // ====================================
      // ATTENDANCE MODULE
      // ====================================
      {
        path: 'attendance',
        children: [
          { index: true, element: <PageSuspense><AttendanceMarking /></PageSuspense> },
          { path: 'mark', element: <PageSuspense><AttendanceMarking /></PageSuspense> },
          { path: 'student/mark', element: <PageSuspense><AttendanceMarking /></PageSuspense> },
          { path: 'student/bulk-view', element: <PageSuspense><BulkAttendanceView /></PageSuspense> },
          { path: 'staff', element: <PageSuspense><StaffAttendance /></PageSuspense> },
          { path: 'reports', element: <PageSuspense><AttendanceReports /></PageSuspense> },
          { path: 'leave', element: <PageSuspense><AttendanceLeaveManagement /></PageSuspense> },
          { path: 'leave/requests', element: <PageSuspense><LeaveRequests /></PageSuspense> },
          { path: 'leave/approval', element: <PageSuspense><LeaveApproval /></PageSuspense> },
          { path: 'leave/balance', element: <PageSuspense><LeaveBalance /></PageSuspense> },
          { path: 'reports/daily', element: <PageSuspense><DailyReport /></PageSuspense> },
          { path: 'reports/monthly', element: <PageSuspense><MonthlyReport /></PageSuspense> },
          { path: 'reports/absentee', element: <PageSuspense><AbsenteeReport /></PageSuspense> },
          { path: 'reports/intelligence', element: <PageSuspense><IntelligenceReport /></PageSuspense> },
          // W7 — consolidated pages
          { path: 'leave-students', element: <PageSuspense><LeaveCenterStudents /></PageSuspense> },
          { path: 'devices', element: <PageSuspense><DevicesAndBiometric /></PageSuspense> },
          // E4 — Attendance advanced business logic
          { path: 'calendar', element: <PageSuspense><E4AcademicCalendar /></PageSuspense> },
          { path: 'late-rules', element: <PageSuspense><E4LateEntryRules /></PageSuspense> },
          { path: 'outdoor-exemptions', element: <PageSuspense><E4OutdoorExemptions /></PageSuspense> },
          { path: 'mass-bunk', element: <PageSuspense><E4MassBunkReview /></PageSuspense> },
          { path: 'early-leave', element: <PageSuspense><E4EarlyLeaveSignout /></PageSuspense> },
          { path: 'subject-attendance', element: <PageSuspense><E4SubjectAttendance /></PageSuspense> },
          { path: 'geofence', element: <PageSuspense><E4GeofenceConfig /></PageSuspense> },
        ],
      },

      // W7 — mid-day meal register (top-level for class-teacher quick access)
      {
        path: 'midday-meal',
        element: <PageSuspense><MidDayMealRegister /></PageSuspense>,
      },

      // ====================================
      // EXAMINATION MODULE
      // ====================================
      {
        path: 'examination',
        children: [
          { index: true, element: <PageSuspense><MarksEntryGrid /></PageSuspense> },
          { path: 'marks', element: <PageSuspense><MarksEntryGrid /></PageSuspense> },
          { path: 'marks-entry', element: <PageSuspense><MarksEntryGrid /></PageSuspense> },
          { path: 'exams', element: <PageSuspense><ExamSchedule /></PageSuspense> },
          { path: 'schedule', element: <PageSuspense><ExamSchedule /></PageSuspense> },
          { path: 'grades', element: <PageSuspense><GradeConfiguration /></PageSuspense> },
          { path: 'report-cards', element: <PageSuspense><ReportCards /></PageSuspense> },
          { path: 'hall-tickets', element: <PageSuspense><HallTickets /></PageSuspense> },
          { path: 'class-mapping', element: <PageSuspense><ClassExamMapping /></PageSuspense> },
          { path: 'marks-moderation', element: <PageSuspense><MarksModeration /></PageSuspense> },
          { path: 'grade-scales', element: <PageSuspense><GradeScales /></PageSuspense> },
          { path: 'report-card/designer', element: <PageSuspense><ReportDesigner /></PageSuspense> },
          { path: 'report-card/generate', element: <PageSuspense><GenerateReports /></PageSuspense> },
          { path: 'report-card/publish', element: <PageSuspense><PublishResults /></PageSuspense> },
          { path: 'reports/performance', element: <PageSuspense><PerformanceAnalysis /></PageSuspense> },
          { path: 'reports/subject-analysis', element: <PageSuspense><SubjectAnalysis /></PageSuspense> },
          { path: 'reports/toppers', element: <PageSuspense><ToppersList /></PageSuspense> },
          { path: 'dashboard', element: <PageSuspense><ExamDashboard /></PageSuspense> },
          { path: 'seating-plan', element: <PageSuspense><SeatingPlan /></PageSuspense> },
          // Phase 2 W6 — Exam single-pane shells (legacy routes above stay live)
          { path: 'setup', element: <PageSuspense><ExamSetupCenter /></PageSuspense> },
          { path: 'marks-entry-moderation', element: <PageSuspense><MarksEntryAndModeration /></PageSuspense> },
          { path: 'results-and-report-cards', element: <PageSuspense><ResultsAndReportCards /></PageSuspense> },
          { path: 'hall-tickets-seating', element: <PageSuspense><HallTicketsAndSeating /></PageSuspense> },
          { path: 'analytics', element: <PageSuspense><ExamAnalytics /></PageSuspense> },
          // Stream E3 — advanced business logic
          { path: 'marks-split', element: <PageSuspense><MarksSplitEditor /></PageSuspense> },
          { path: 'streams', element: <PageSuspense><StreamConfiguration /></PageSuspense> },
          { path: 'supplementary', element: <PageSuspense><SupplementaryExams /></PageSuspense> },
          { path: 'moderation', element: <PageSuspense><BellCurveModeration /></PageSuspense> },
          { path: 'practical-scheduling', element: <PageSuspense><PracticalScheduling /></PageSuspense> },
          { path: 'exemptions', element: <PageSuspense><ExamExemptions /></PageSuspense> },
          // Wave 1 — bulk report-card generation
          { path: 'report-card-batches', element: <PageSuspense><ReportCardBatches /></PageSuspense> },
          // W1 — Question Paper Studio (centerpiece)
          { path: 'question-studio', element: <PageSuspense><QuestionPaperStudio /></PageSuspense> },
          { path: 'question-papers', element: <PageSuspense><QuestionPaperStudio /></PageSuspense> },
          // NEP 2020 Holistic Progress Card — teacher entry grid, compile+PDF,
          // and the parent/student read-only view (role-branches internally).
          { path: 'holistic-progress-card', element: <PageSuspense><HolisticProgressCard /></PageSuspense> },
        ],
      },

      // ====================================
      // FINANCE MODULE
      // ====================================
      {
        path: 'finance',
        children: [
          { index: true, element: <PageSuspense><FeeCollectionPOS /></PageSuspense> },
          { path: 'fee-collection', element: <PageSuspense><FeeCollectionPOS /></PageSuspense> },
          { path: 'collection', element: <PageSuspense><FeeCollectionPOS /></PageSuspense> },
          { path: 'pos', element: <PageSuspense><FeeCollectionPOS /></PageSuspense> },
          // Fee Designer Studio (D4) — absorbs FeeStructure/FeeComponents/
          // FeeConfiguration/FeeTerms into one deep-linkable 4-step flow.
          { path: 'fee-designer', element: <PageSuspense><FeeDesignerStudio /></PageSuspense> },
          { path: 'fee-structure', element: <Navigate to="/finance/fee-designer?step=structures" replace /> },
          { path: 'fee-structures', element: <Navigate to="/finance/fee-designer?step=structures" replace /> },
          { path: 'components', element: <Navigate to="/finance/fee-designer?step=categories" replace /> },
          { path: 'fee-components', element: <Navigate to="/finance/fee-designer?step=categories" replace /> },
          { path: 'student-ledger', element: <PageSuspense><StudentLedger /></PageSuspense> },
          { path: 'receipts', element: <PageSuspense><ReceiptArchive /></PageSuspense> },
          { path: 'concessions', element: <PageSuspense><Concessions /></PageSuspense> },
          { path: 'discounts', element: <PageSuspense><DiscountsWaivers /></PageSuspense> },
          { path: 'defaulters', element: <PageSuspense><FeeDefaulters /></PageSuspense> },
          { path: 'reports', element: <PageSuspense><FinanceReports /></PageSuspense> },
          { path: 'expenses', element: <PageSuspense><Expenses /></PageSuspense> },
          { path: 'dashboard', element: <PageSuspense><FinanceDashboard /></PageSuspense> },
          { path: 'reports/collection', element: <PageSuspense><CollectionReport /></PageSuspense> },
          { path: 'reports/outstanding', element: <PageSuspense><OutstandingReport /></PageSuspense> },
          { path: 'reports/class-wise', element: <PageSuspense><ClassWiseReport /></PageSuspense> },
          { path: 'configuration', element: <Navigate to="/finance/fee-designer?step=assign" replace /> },
          { path: 'tax-config', element: <PageSuspense><TaxConfiguration /></PageSuspense> },
          // Previously-orphaned accounting pages, now reachable.
          { path: 'bank-reconciliation', element: <PageSuspense><BankReconciliation /></PageSuspense> },
          { path: 'trial-balance', element: <PageSuspense><TrialBalance /></PageSuspense> },
          { path: 'gl-reconciliation', element: <PageSuspense><GlReconciliation /></PageSuspense> },
          { path: 'gst-report', element: <PageSuspense><GstReport /></PageSuspense> },
          { path: 'vendor-tds', element: <PageSuspense><VendorTdsReport /></PageSuspense> },
          { path: 'profit-loss', element: <PageSuspense><ProfitLoss /></PageSuspense> },
          { path: 'balance-sheet', element: <PageSuspense><BalanceSheet /></PageSuspense> },
          { path: 'cash-flow', element: <PageSuspense><CashFlow /></PageSuspense> },
          { path: 'journal-entry', element: <PageSuspense><JournalEntry /></PageSuspense> },
          // E2 — advanced fees pages
          { path: 'terms', element: <Navigate to="/finance/fee-designer?step=terms" replace /> },
          { path: 'bundles', element: <PageSuspense><FeeBundles /></PageSuspense> },
          { path: 'refunds', element: <PageSuspense><RefundManagement /></PageSuspense> },
          { path: 'rollover', element: <PageSuspense><YearRollover /></PageSuspense> },
          { path: 'cheque-bounces', element: <PageSuspense><ChequeBounceQueue /></PageSuspense> },
          // Wave 1 — installment plans, scholarships, audit trail
          { path: 'installment-plans', element: <PageSuspense><InstallmentPlans /></PageSuspense> },
          { path: 'scholarships', element: <PageSuspense><Scholarships /></PageSuspense> },
          { path: 'audit-trail', element: <PageSuspense><FeeAuditTrail /></PageSuspense> },
        ],
      },

      // ====================================
      // HR MODULE
      // ====================================
      {
        path: 'hr',
        children: [
          { index: true, element: <EmployeeList /> },
          { path: 'employees', element: <EmployeeList /> },
          { path: 'employees/new', element: <PageSuspense><EmployeeNew /></PageSuspense> },
          { path: 'employees/:id/edit', element: <PageSuspense><EmployeeNew /></PageSuspense> },
          // Previously-orphaned consolidation pages, now reachable.
          { path: 'people-operations', element: <PageSuspense><PeopleOperationsCenter /></PageSuspense> },
          { path: 'talent-center', element: <PageSuspense><TalentCenter /></PageSuspense> },
          { path: 'payroll', element: <PageSuspense><PayrollProcessor /></PageSuspense> },
          { path: 'leave', element: <PageSuspense><LeaveManagement /></PageSuspense> },
          { path: 'recruitment', element: <PageSuspense><Recruitment /></PageSuspense> },
          { path: 'performance', element: <PageSuspense><Performance /></PageSuspense> },
          { path: 'separation', element: <PageSuspense><EmployeeSeparation /></PageSuspense> },
          { path: 'departments', element: <PageSuspense><Departments /></PageSuspense> },
          { path: 'designations', element: <PageSuspense><Designations /></PageSuspense> },
          { path: 'salary-structures', element: <PageSuspense><SalaryStructures /></PageSuspense> },
          { path: 'salary-structures/designer', element: <PageSuspense><SalaryStructureDesigner /></PageSuspense> },
          { path: 'salary-structures/:id/designer', element: <PageSuspense><SalaryStructureDesigner /></PageSuspense> },
          { path: 'salary-components', element: <PageSuspense><SalaryComponents /></PageSuspense> },
          { path: 'salary-advances', element: <PageSuspense><SalaryAdvances /></PageSuspense> },
          { path: 'reimbursements', element: <PageSuspense><ReimbursementClaims /></PageSuspense> },
          { path: 'gratuity', element: <PageSuspense><Gratuity /></PageSuspense> },
          { path: 'payslips', element: <PageSuspense><Payslips /></PageSuspense> },
          { path: 'compliance', element: <PageSuspense><ComplianceSettings /></PageSuspense> },
          { path: 'bonus-report', element: <PageSuspense><BonusReport /></PageSuspense> },
          { path: 'payroll-config', element: <PageSuspense><PayrollConfig /></PageSuspense> },
          { path: 'dashboard', element: <PageSuspense><HRDashboard /></PageSuspense> },
          { path: 'employees/:id', element: <PageSuspense><EmployeeProfile /></PageSuspense> },
          { path: 'employees/:id/profile', element: <PageSuspense><EmployeeProfile /></PageSuspense> },
          { path: 'employees/:id/documents', element: <PageSuspense><EmployeeDocumentsRoute /></PageSuspense> },
          { path: 'reports/salary-register', element: <PageSuspense><SalaryRegister /></PageSuspense> },
          { path: 'reports/headcount', element: <PageSuspense><HeadcountReport /></PageSuspense> },
          // Stream E5 — advanced HR/Payroll business logic
          { path: 'da-revision', element: <PageSuspense><DARevision /></PageSuspense> },
          { path: 'annual-increments', element: <PageSuspense><AnnualIncrements /></PageSuspense> },
          { path: 'salary-arrears', element: <PageSuspense><SalaryArrears /></PageSuspense> },
          { path: 'leave-encashment', element: <PageSuspense><LeaveEncashment /></PageSuspense> },
          { path: 'loans', element: <PageSuspense><EmployeeLoansE5 /></PageSuspense> },
          { path: 'bank-file-export', element: <PageSuspense><BankFileExportE5 /></PageSuspense> },
          { path: 'form24q', element: <PageSuspense><Form24QFilingE5 /></PageSuspense> },
        ],
      },

      // ====================================
      // TRANSPORT MODULE
      // ====================================
      {
        path: 'transport',
        children: [
          { index: true, element: <PageSuspense><TransportManagement /></PageSuspense> },
          { path: 'routes', element: <PageSuspense><TransportManagement /></PageSuspense> },
          { path: 'vehicles', element: <PageSuspense><TransportVehicles /></PageSuspense> },
          { path: 'tracking', element: <PageSuspense><LiveTracking /></PageSuspense> },
          { path: 'fees', element: <PageSuspense><TransportFees /></PageSuspense> },
          { path: 'student-assignment', element: <PageSuspense><StudentAssignment /></PageSuspense> },
          { path: 'billing', element: <PageSuspense><TransportBilling /></PageSuspense> },
          { path: 'reports/route-wise', element: <PageSuspense><RouteWiseReport /></PageSuspense> },
          { path: 'reports/utilization', element: <PageSuspense><UtilizationReport /></PageSuspense> },
        ],
      },

      // ====================================
      // LIBRARY MODULE
      // ====================================
      {
        path: 'library',
        children: [
          { index: true, element: <PageSuspense><LibraryManagement /></PageSuspense> },
          { path: 'books', element: <PageSuspense><LibraryManagement /></PageSuspense> },
          { path: 'issue', element: <PageSuspense><LibraryIssueReturn /></PageSuspense> },
          { path: 'catalog', element: <PageSuspense><LibraryCatalog /></PageSuspense> },
          { path: 'reports', element: <PageSuspense><LibraryReports /></PageSuspense> },
          { path: 'transactions', element: <PageSuspense><Transactions /></PageSuspense> },
          { path: 'fines', element: <PageSuspense><Fines /></PageSuspense> },
          { path: 'reports/circulation', element: <PageSuspense><CirculationReport /></PageSuspense> },
          { path: 'reports/overdue', element: <PageSuspense><OverdueReport /></PageSuspense> },
          { path: 'reports/popular', element: <PageSuspense><PopularBooks /></PageSuspense> },
        ],
      },,

      // ====================================
      // HOSTEL MODULE
      // ====================================
      {
        path: 'hostel',
        children: [
          { index: true, element: <PageSuspense><HostelRooms /></PageSuspense> },
          { path: 'rooms', element: <PageSuspense><HostelRooms /></PageSuspense> },
          { path: 'allocation', element: <PageSuspense><HostelAllocation /></PageSuspense> },
          { path: 'fees', element: <PageSuspense><HostelFees /></PageSuspense> },
          { path: 'mess', element: <PageSuspense><MessManagement /></PageSuspense> },
        ],
      },

      // ====================================
      // COMMUNICATION MODULE
      // ====================================
      {
        path: 'communication',
        children: [
          // W9 Phase 2 — comms_* pipeline (default landing = console)
          { index: true, element: <PageSuspense><CommsConsole /></PageSuspense> },
          { path: 'compose', element: <PageSuspense><ComposeAndSend /></PageSuspense> },
          { path: 'templates', element: <PageSuspense><TemplatesManager /></PageSuspense> },
          { path: 'messages', element: <PageSuspense><Messages /></PageSuspense> },
          { path: 'messages/:id', element: <PageSuspense><MessageThread /></PageSuspense> },
          { path: 'dlq', element: <PageSuspense><DeliveryLogsAndDLQ /></PageSuspense> },
          { path: 'consent-dnd', element: <PageSuspense><ConsentAndDNDCenter /></PageSuspense> },
          { path: 'ptm', element: <PageSuspense><PTMManager /></PageSuspense> },
          // Legacy routes preserved for backward compatibility
          { path: 'announcements', element: <PageSuspense><Announcements /></PageSuspense> },
          { path: 'sms', element: <PageSuspense><SMSGateway /></PageSuspense> },
          { path: 'email', element: <PageSuspense><EmailTemplates /></PageSuspense> },
          { path: 'templates-legacy', element: <PageSuspense><Templates /></PageSuspense> },
        ],
      },

      // ====================================
      // SETTINGS MODULE
      // ====================================
      {
        path: 'settings',
        children: [
          { index: true, element: <PageSuspense><GeneralSettings /></PageSuspense> },
          { path: 'general', element: <PageSuspense><GeneralSettings /></PageSuspense> },
          { path: 'profile', element: <PageSuspense><MyProfile /></PageSuspense> },
          { path: 'users', element: <PageSuspense><UserManagement /></PageSuspense> },
          { path: 'roles', element: <PageSuspense><RolesPermissions /></PageSuspense> },
          { path: 'school', element: <PageSuspense><SchoolSettings /></PageSuspense> },
          { path: 'branches', element: <PageSuspense><Branches /></PageSuspense> },
          { path: 'academic-years', element: <PageSuspense><AcademicYears /></PageSuspense> },
          { path: 'section-assignment', element: <PageSuspense><SectionAssignmentRules /></PageSuspense> },
          { path: 'integrations', element: <PageSuspense><Integrations /></PageSuspense> },
          // Communication providers were merged into the unified Integrations page.
          { path: 'communication-integrations', element: <Navigate to="/settings/integrations" replace /> },
          { path: 'notification-templates', element: <PageSuspense><NotificationTemplates /></PageSuspense> },
          { path: 'custom-fields', element: <PageSuspense><CustomFields /></PageSuspense> },
          { path: 'audit-logs', element: <PageSuspense><AuditLogs /></PageSuspense> },
          { path: 'approval-workflows', element: <PageSuspense><ApprovalWorkflows /></PageSuspense> },
          { path: 'setup', element: <PageSuspense><SetupWizard /></PageSuspense> },
        ],
      },

      // ====================================
      // VISUAL TEMPLATE EDITOR
      // Receipts, payslips, report cards, hall tickets, ID cards,
      // certificates, exam papers — all designed in one schema-driven
      // editor and rendered server-side for live preview + PDF parity.
      // ====================================
      {
        path: 'templates',
        children: [
          { index: true, element: <PageSuspense><TemplateLibrary /></PageSuspense> },
          { path: ':docType/:id', element: <PageSuspense><TemplateEditorPage /></PageSuspense> },
        ],
      },

      // ====================================
      // REPORTS MODULE
      // ====================================
      {
        path: 'reports',
        children: [
          { index: true, element: <PageSuspense><ReportsDashboard /></PageSuspense> },
          { path: 'dashboard', element: <PageSuspense><ReportsDashboard /></PageSuspense> },
          { path: 'custom', element: <PageSuspense><CustomReports /></PageSuspense> },
          { path: 'consolidated', element: <PageSuspense><ConsolidatedReports /></PageSuspense> },
          { path: 'report-builder', element: <PageSuspense><ReportBuilder /></PageSuspense> },
        ],
      },

      // ====================================
      // INVENTORY / STORE MODULE
      // ====================================
      {
        path: 'inventory',
        children: [
          { index: true, element: <PageSuspense><Items /></PageSuspense> },
          { path: 'items', element: <PageSuspense><Items /></PageSuspense> },
          { path: 'vendors', element: <PageSuspense><Vendors /></PageSuspense> },
          { path: 'purchase-orders', element: <PageSuspense><PurchaseOrders /></PageSuspense> },
          { path: 'purchase', element: <PageSuspense><PurchaseOrders /></PageSuspense> },
          { path: 'stock-movements', element: <PageSuspense><StockMovements /></PageSuspense> },
          { path: 'assets', element: <PageSuspense><Assets /></PageSuspense> },
          { path: 'asset-allocation', element: <PageSuspense><AssetAllocation /></PageSuspense> },
          { path: 'assets/allocation', element: <PageSuspense><AssetAllocation /></PageSuspense> },
          { path: 'reports/stock', element: <PageSuspense><StockReport /></PageSuspense> },
          { path: 'reports/low-stock', element: <PageSuspense><LowStockReport /></PageSuspense> },
          { path: 'reports/asset-register', element: <PageSuspense><AssetRegister /></PageSuspense> },
          { path: 'reports/audit-trail', element: <PageSuspense><AuditTrail /></PageSuspense> },
        ],
      },

    ],
  },

  // ========================================
  // CATCH ALL - 404
  // ========================================
  {
    path: '*',
    element: <Suspense fallback={<div className="flex justify-center p-8"><div className="size-8 animate-spin-slow rounded-full border-2 border-primary/30 border-t-primary" /></div>}><NotFound /></Suspense>,
  },
]);

export default router;
